// frontend/src/services/catalog.ts
//
// 商品目录(Product/Price)与收银会话(Checkout Session)服务。
// 后端契约：
//   商户 JWT 组 /merchant/catalog/products、/merchant/catalog/prices、/merchant/checkout-sessions
//   收银台公开组 /public/checkout-session?token=（无需登录）
// 响应统一为 {success, message, data}（列表带 meta 分页）。

import ApiService from './api';
import { safeArray } from '../utils/money';

// ---- 类型 ----

export type ProductType = 'digital' | 'service' | 'good';
export type PriceType = 'one_time' | 'recurring';
export type RecurringInterval = 'day' | 'week' | 'month' | 'year';
export type CheckoutMode = 'payment' | 'subscription' | 'setup';
export type CheckoutPresentation = 'iframe' | 'link';

export interface Product {
  id: string;
  name: string;
  description?: string;
  type: ProductType;
  tax_category?: string;
  images?: string[];
  active: boolean;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface Price {
  id: string;
  product_id: string;
  currency: string;
  unit_amount: number; // 最小货币单位（分）
  type: PriceType;
  recurring_interval?: RecurringInterval;
  recurring_interval_count?: number;
  trial_period_days?: number;
  active: boolean;
  nickname?: string;
  // SKU / 多规格
  sku?: string;
  variant_name?: string; // 规格名，如"红色/L"
  attributes?: Record<string, string>; // 规格属性键值
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface CheckoutSession {
  id: string;
  mode: CheckoutMode;
  presentation?: CheckoutPresentation;
  status: 'open' | 'complete' | 'expired' | 'canceled';
  customer_id?: string;
  customer_email?: string;
  amount_subtotal: number;
  amount_tax: number;
  amount_total: number;
  currency?: string;
  success_url?: string;
  cancel_url?: string;
  payment_intent_id?: string;
  subscription_id?: string;
  client_reference_id?: string;
  session_token?: string;
  expires_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CheckoutLineItemParam {
  price_id?: string;
  product_id?: string;
  quantity: number;
  ad_hoc_amount?: number;
  ad_hoc_currency?: string;
  description?: string;
}

export interface CreateCheckoutSessionRequest {
  mode: CheckoutMode;
  presentation?: CheckoutPresentation;
  line_items: CheckoutLineItemParam[];
  customer_id?: string;
  customer_email?: string;
  success_url: string;
  cancel_url: string;
  client_reference_id?: string;
  buyer_country?: string;
  metadata?: Record<string, string>;
}

export interface CheckoutSessionResult {
  session: CheckoutSession;
  url: string;
  session_token: string;
}

// 收银台公开展示数据（凭 session_token 获取，已脱敏）
export interface PublicCheckoutLineItem {
  name: string;
  description?: string;
  variant_name?: string; // 规格名，让买家看到买的哪个规格
  sku?: string;
  quantity: number;
  unit_amount: number;
  currency: string;
}

export interface PublicCheckoutSession {
  id: string;
  mode: CheckoutMode;
  status: string;
  merchant_name: string;
  currency: string;
  amount_subtotal: number;
  amount_tax: number;
  amount_total: number;
  customer_email?: string;
  line_items: PublicCheckoutLineItem[];
  expires_at?: string;
}

// 支付方式集成类型：
//   stripe_element 走 Stripe Payment Element（card/钱包/银行/分期等共用一个 Element）
//   redirect       自有跳转渠道（如银联）
//   qr             自有扫码渠道（微信/支付宝）
//   crypto         加密货币转账
export type CheckoutIntegrationType = 'stripe_element' | 'redirect' | 'qr' | 'crypto';

// prepare 返回的单个支付方式
export interface CheckoutMethodOption {
  method: string; // 如 card / apple_pay / wechat / unionpay / usdt
  label: string; // 展示名（白标，已是我们自己的文案）
  provider: string; // stripe / epay / crypto
  integration_type: CheckoutIntegrationType;
}

// 收银台收卡准备结果（payment 模式）
//   client_secret / publishable_key 仅承载 provider==stripe 的方式，供 Payment Element 用
//   methods 为该会话启用的全部支付方式清单
export interface CheckoutPrepareResult {
  payment_intent_id: string;
  client_secret: string;
  publishable_key: string;
  amount: number;
  currency: string;
  country?: string; // 收款账户国家（两位 ISO），钱包按钮 PaymentRequest 用
  stripe_account?: string; // PSP/Connect 商户的连接账户 id（direct charge，前端 Stripe.js 需用它初始化）
  methods?: CheckoutMethodOption[];
}

// 自有渠道发起结果（pay-own-channel）
export interface OwnChannelResult {
  integration_type: CheckoutIntegrationType;
  redirect_url?: string;
  qr_content?: string;
  crypto_address?: string;
  crypto_amount?: string;
  crypto_chain?: string;
  reference?: string;
}

export interface ListMeta {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

const DEFAULT_META: ListMeta = { total: 0, per_page: 20, current_page: 1, last_page: 1 };

// ---- 商品 / 价格 ----

export class CatalogService {
  /** 创建商品 */
  static async createProduct(data: Partial<Product>): Promise<Product> {
    const response = await ApiService.post<Product>('/merchant/catalog/products', data);
    return response.data;
  }

  /** 商品列表 */
  static async listProducts(
    page: number = 1,
    perPage: number = 20,
    active?: boolean
  ): Promise<{ data: Product[]; meta: ListMeta }> {
    const params: any = { page, per_page: perPage };
    if (active !== undefined) params.active = active;
    const response = await ApiService.get<Product[]>('/merchant/catalog/products', params);
    return { data: safeArray<Product>(response.data), meta: (response.meta as ListMeta) || DEFAULT_META };
  }

  /** 商品详情 */
  static async getProduct(id: string): Promise<Product> {
    const response = await ApiService.get<Product>(`/merchant/catalog/products/${id}`);
    return response.data;
  }

  /** 更新商品 */
  static async updateProduct(id: string, data: Partial<Product>): Promise<Product> {
    const response = await ApiService.put<Product>(`/merchant/catalog/products/${id}`, data);
    return response.data;
  }

  /** 停用商品 */
  static async deactivateProduct(id: string): Promise<void> {
    await ApiService.delete(`/merchant/catalog/products/${id}`);
  }

  /** 创建价格 */
  static async createPrice(data: Partial<Price>): Promise<Price> {
    const response = await ApiService.post<Price>('/merchant/catalog/prices', data);
    return response.data;
  }

  /** 价格列表（可按商品过滤） */
  static async listPrices(
    productId?: string,
    page: number = 1,
    perPage: number = 100,
    active?: boolean
  ): Promise<{ data: Price[]; meta: ListMeta }> {
    const params: any = { page, per_page: perPage };
    if (productId) params.product_id = productId;
    if (active !== undefined) params.active = active;
    const response = await ApiService.get<Price[]>('/merchant/catalog/prices', params);
    return { data: safeArray<Price>(response.data), meta: (response.meta as ListMeta) || DEFAULT_META };
  }

  /** 价格详情 */
  static async getPrice(id: string): Promise<Price> {
    const response = await ApiService.get<Price>(`/merchant/catalog/prices/${id}`);
    return response.data;
  }

  /** 更新价格 */
  static async updatePrice(id: string, data: Partial<Price>): Promise<Price> {
    const response = await ApiService.put<Price>(`/merchant/catalog/prices/${id}`, data);
    return response.data;
  }

  /** 停用价格 */
  static async deactivatePrice(id: string): Promise<void> {
    await ApiService.delete(`/merchant/catalog/prices/${id}`);
  }

  // ---- 收银会话（商户侧） ----

  /** 创建收银会话 */
  static async createCheckoutSession(
    data: CreateCheckoutSessionRequest
  ): Promise<CheckoutSessionResult> {
    const response = await ApiService.post<CheckoutSessionResult>(
      '/merchant/checkout-sessions',
      data
    );
    return response.data;
  }

  /** 收银会话列表 */
  static async listCheckoutSessions(
    page: number = 1,
    perPage: number = 20,
    status?: string
  ): Promise<{ data: CheckoutSession[]; meta: ListMeta }> {
    const params: any = { page, per_page: perPage };
    if (status) params.status = status;
    const response = await ApiService.get<CheckoutSession[]>('/merchant/checkout-sessions', params);
    return { data: safeArray<CheckoutSession>(response.data), meta: (response.meta as ListMeta) || DEFAULT_META };
  }

  /** 收银会话详情 */
  static async getCheckoutSession(id: string): Promise<CheckoutSession> {
    const response = await ApiService.get<CheckoutSession>(`/merchant/checkout-sessions/${id}`);
    return response.data;
  }

  // ---- 收银台公开端点（凭 session_token，无需登录） ----

  /** 凭 token 获取脱敏会话展示数据 */
  static async getPublicCheckoutSession(token: string): Promise<PublicCheckoutSession> {
    const response = await ApiService.get<PublicCheckoutSession>('/public/checkout-session', {
      token,
    });
    return response.data;
  }

  /** 凭 token 确认会话（body 可空或带 payment_method_id / customer_id） */
  static async confirmPublicCheckoutSession(
    token: string,
    body?: { payment_method_id?: string; customer_id?: string }
  ): Promise<any> {
    const response = await ApiService.post<any>(
      `/public/checkout-session/confirm?token=${encodeURIComponent(token)}`,
      body || {}
    );
    return response.data;
  }

  /** 凭 token 为 payment 模式准备收款，返回 client_secret/publishable_key（供 Payment Element）+ methods 方式清单 */
  static async preparePublicCheckoutPayment(token: string): Promise<CheckoutPrepareResult> {
    const response = await ApiService.post<CheckoutPrepareResult>(
      `/public/checkout-session/prepare?token=${encodeURIComponent(token)}`,
      {}
    );
    return response.data;
  }

  /** 凭 token 为「其他支付方式」准备 Payment Element 专用 PI（排除 card/link/cashapp）。懒加载：选中该入口时才调。 */
  static async preparePublicCheckoutElement(token: string): Promise<CheckoutPrepareResult> {
    const response = await ApiService.post<CheckoutPrepareResult>(
      `/public/checkout-session/prepare-element?token=${encodeURIComponent(token)}`,
      {}
    );
    return response.data;
  }

  /** 凭 token 发起自有渠道支付（qr/redirect/crypto）。后端未配该渠道时可能返回 501。 */
  static async payOwnChannel(token: string, method: string): Promise<OwnChannelResult> {
    const response = await ApiService.post<OwnChannelResult>(
      '/public/checkout-session/pay-own-channel',
      { token, method }
    );
    return response.data;
  }
}

export default CatalogService;
